
import Vue from 'vue'
import { Component, Inject, Prop, Watch } from 'vue-property-decorator';
import SatellitalEvolutionChart from '../../claim-blocks/satellital/SatellitalEvolutionChart.vue';
import SinisterMapLayers from '../../claim-blocks/SinisterMapLayers.vue';
import SinisterInspection from '../../claim-blocks/SinisterInspection.vue';
import SummaryItems from '../../claim-blocks/summary/SummaryItems.vue';
import SinisterNotes from '../../claim-blocks/SinisterNotes.vue';
import { InjectionKey } from '../../../core/injection-keys';
import { IndicatorConfig, TenantConfig } from '../../../tenants/types';
import { IndicatorValues } from '../../../core/utils/indicator-format';
import { SatellitalLayersInfo } from '../../../shared/types/satellital/layers';
import { ClaimDetailResponse, UpdateClaimPolygonResponse } from '../../../core/api/ports';
import ItemIndicators from '../../item/components/ItemIndicators.vue';
import { FeatureCollection } from '../../../shared/types/MapShapes';

@Component({
    components: {
        ItemIndicators,
        SinisterMapLayers,
        SinisterInspection,
        SummaryItems,
        SinisterNotes,
        SatellitalEvolutionChart,
    }
})
export default class ClaimDetailView extends Vue {
    @Inject(InjectionKey.Tenant) readonly tenant!: TenantConfig;
    @Prop({ required: true }) claimInfo!: ClaimDetailResponse;
    @Prop({ default: () => null }) itemPolygon: any;

    localClaimInfo: ClaimDetailResponse | null = null;
    localItemPolygon: FeatureCollection | null = null;
    // Nota seleccionada / en hover, compartida entre mapa y listado de notas
    selectedNoteId: number | null = null;
    hoveredNoteId: number | null = null;

    satellitalLayersInfo: SatellitalLayersInfo = {
        zonified: {
            date: null,
            png_url: null,
            total_area: null,
            bar_url: '',
            zones: [],
            bounds: null
        },
        ndvi: {
            initial_date: null,
            final_date: null,
            png_url: null,
            bar_url: '',
            total_area: null,
            zones: [],
            bounds: null
        }
    }

    mounted() {
        // Create a local copy of claimInfo
        this.localClaimInfo = { ...this.claimInfo };
        this.localItemPolygon = this.itemPolygon;
    }

    // Otro siniestro: la selección de notas no pasa de uno a otro
    @Watch('claimInfo.id')
    onClaimChange() {
        this.selectedNoteId = null;
        this.hoveredNoteId = null;
    }

    selectNote(noteId: number | null) {
        this.selectedNoteId = noteId;
    }

    hoverNote(noteId: number | null) {
        this.hoveredNoteId = noteId;
    }

    get activeClaimInfo() {
        return this.localClaimInfo || this.claimInfo;
    }

    get indicatorsConfig(): IndicatorConfig[] {
        return this.tenant.pages.claimDetail ? this.tenant.pages.claimDetail.indicators : [];
    }

    /** Modelo común de indicadores: valores crudos (el formato lo aplica `ItemIndicators` según el config). */
    get claimIndicators(): IndicatorValues {
        return {
            creation_date: this.activeClaimInfo.creation_date,
            claim_id: this.activeClaimInfo.claim_id,
            event_type: this.getEventTypeName(this.activeClaimInfo.event_type),
            event_date: this.activeClaimInfo.event_date,
            crop_type: this.getCropTypeName(this.activeClaimInfo.crop_type),
            declared_area: this.activeClaimInfo.declared_area,
            area: this.activeClaimInfo.area
        }
    }

    get sinisterWithAppraisers() {
        // Transform appraisers string array to Appraiser objects
        const appraisers_list = (this.activeClaimInfo.appraisers || []).map(email => ({ email }));
        return {
            ...this.activeClaimInfo,
            appraisers_list
        };
    }

    onPolygonUpdated(payload: Pick<UpdateClaimPolygonResponse, 'ranch_area' | 'polygon' | 'using_original_polygon'>) {
        if (this.localClaimInfo) {
            this.localClaimInfo = {
                ...this.localClaimInfo,
                area: payload.ranch_area,
                ...(payload.polygon ? { polygon: payload.polygon } : {}),
                ...(payload.using_original_polygon !== undefined ? { using_original_polygon: payload.using_original_polygon } : {})
            };
        }
        if (payload.polygon) {
            this.localItemPolygon = payload.polygon;
        }
    }

    get eventTypeTranslator() {
        return this.$store.getters['properties/getEventTypeName'];
    }

    get cropTypeTranslator() {
        return this.$store.getters['properties/getCropTypeName'];
    }

    getEventTypeName(eventType: number): string {
        if (this.eventTypeTranslator) {
            return this.eventTypeTranslator(eventType);
        }
        return eventType && eventType.toString() || '';
    }

    getCropTypeName(cropType: number): string {
        if (this.cropTypeTranslator) {
            return this.cropTypeTranslator(cropType);
        }
        return cropType && cropType.toString() || '';
    }


    seeNotesInMap(notes) {
        const mapComponent = this.$refs.map as any;
        if (mapComponent && typeof mapComponent.seeNotesInMap === 'function') {
            mapComponent.seeNotesInMap(notes);
        }
    }

    onClickedChartPoint(pointIndex: number) {
        const mapComponent = this.$refs.map as any;
        if (mapComponent && typeof mapComponent.clickedSatelliteImage === 'function') {
            mapComponent.clickedSatelliteImage(pointIndex);
        }
    }

    updateCarouselIndex(idx: number) {
        const chartComponent = this.$refs.chart as any;
        if (chartComponent && typeof chartComponent.updateCarouselIndex === 'function') {
            chartComponent.updateCarouselIndex(idx);
        }
    }

    updateSelectedImageInChart(img: any) {
        const chartComponent = this.$refs.chart as any;
        if (chartComponent && typeof chartComponent.updateSelectedImage === 'function') {
            chartComponent.updateSelectedImage(img);
        }
    }

    updateSelectedBandInChart(band: any) {
        const chartComponent = this.$refs.chart as any;
        if (chartComponent && typeof chartComponent.updateSelectedBand === 'function') {
            chartComponent.updateSelectedBand(band);
        }
    }

    updateLayersInfoInChilds(newLayers: SatellitalLayersInfo) {
        const sumZComponent = this.$refs.sumZ as any;
        const sumNComponent = this.$refs.sumN as any;
        const chartComponent = this.$refs.chart as any;

        if (sumZComponent && typeof sumZComponent.updateLayersInfo === 'function') {
            sumZComponent.updateLayersInfo(newLayers.zonified);
        }
        if (sumNComponent && typeof sumNComponent.updateLayersInfo === 'function') {
            sumNComponent.updateLayersInfo(newLayers.ndvi);
        }
        if (chartComponent && typeof chartComponent.updateSatellitalLayersInfo === 'function') {
            chartComponent.updateSatellitalLayersInfo(newLayers);
        }
    }

    updateLoadingZonification(v: boolean) {
        const sumZComponent = this.$refs.sumZ as any;
        if (sumZComponent && typeof sumZComponent.updateLoadingIndicator === 'function') {
            sumZComponent.updateLoadingIndicator(v);
        }
    }

    updateLoadingNdviDiff(v: boolean) {
        const sumNComponent = this.$refs.sumN as any;
        if (sumNComponent && typeof sumNComponent.updateLoadingIndicator === 'function') {
            sumNComponent.updateLoadingIndicator(v);
        }
    }

    updateLoadingMapInChilds(v: boolean) {
        const chartComponent = this.$refs.chart as any;
        if (chartComponent && typeof chartComponent.updateIsLoadingLayers === 'function') {
            chartComponent.updateIsLoadingLayers(v);
        }
        this.updateLoadingZonification(v);
        this.updateLoadingNdviDiff(v);
    }

    addRgbNdviToChart(values: any) {
        const chartComponent = this.$refs.chart as any;
        if (chartComponent && typeof chartComponent.setRgbAndNdviValues === 'function') {
            chartComponent.setRgbAndNdviValues(values);
        }
    }
}
